import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export const getProcessingStatusText = (startTime?: string) => {
  if (!startTime) return 'We are processing your file...'
  const start = new Date(startTime).getTime()
  const now = Date.now()
  const elapsed = (now - start) / 1000

  if (elapsed < 5) {
    return 'We are processing your file...'
  } else if (elapsed < 10) {
    return 'Extracting OCR data...'
  } else if (elapsed < 15) {
    return 'Extracting invoice fields...'
  } else if (elapsed < 20) {
    return 'Matching fields with Purchase Order...'
  } else if (elapsed < 25) {
    return 'Matching fields with GL Accounts...'
  } else if (elapsed < 30) {
    return 'Verifying supplier details...'
  } else {
    return 'Making the decision...'
  }
}

export const useProcessingStatusText = (startTime?: string) => {
  const [text, setText] = useState(() => getProcessingStatusText(startTime))

  useEffect(() => {
    if (!startTime) return

    const interval = setInterval(() => {
      setText(getProcessingStatusText(startTime))
    }, 1000)

    return () => clearInterval(interval)
  }, [startTime])

  return text
}

const getInitialJobMappings = () => {
  try {
    const data = localStorage.getItem('v6_job_mappings')
    return data ? JSON.parse(data) : {}
  } catch {
    return {}
  }
}

const getInitialJobStatuses = () => {
  try {
    const data = localStorage.getItem('v6_job_statuses')
    return data ? JSON.parse(data) : {}
  } catch {
    return {}
  }
}

type Store = {
  activeQuickFilters: string[]
  activeTabValue: string | null

  isClosed: boolean
  isAutoOpenedRequest: boolean
  // UI State
  isMaximized: boolean
  isRequestOpen: boolean
  jobMappings: Record<string, string>
  jobStatuses: Record<string, any>
  kanbanMissingFieldIds: string[]
  newRequest: boolean
  newRequestMeta: string | null
  pendingDeepLink: {
    processId: string
    tab?: string
    transactionId?: string
    workflowId: string
  } | null
  pendingNav: any
  pendingOpenNewRequest: boolean
  processingProcesses: any[]
  /** Quote edited after its PDF was generated; Regenerate PDF is pending. */
  quoteRegenerationPending: boolean
  rawWorkflowData: any
  /** Name of the workflow whose request list is on screen (topbar title). */
  listWorkflowName: string | null
  /** Bumped when the sidebar Workflows icon asks for the All Workflows inbox. */
  allWorkflowsSignal: number
  reloadMeta: boolean
  repoData: any
  requestListTab: string // New state for main list tabs
  // Data State (Added these)
  selectedItem: any
  // Actions
  selectedWorkflow: any
  selectedWorkflowId: number | string | null
  summaryCache: Record<string, any>
  addProcessingProcess: (process: any) => void
  cacheSummaryData: (reqNo: string, data: any) => void
  clearPendingDeepLink: () => void
  clearPendingNav: () => void
  clearQuickFilters: () => void
  closeNewRequest: () => void
  closeRequest: () => void
  handleSetRepoData: (data: any) => void
  openNewRequest: (title: string) => void
  openRequest: (
    item: any,
    workflowId: any,
    tab: string,
    missingFieldIds?: string[],
    options?: { isAutoOpened?: boolean },
  ) => void
  removeProcessingProcess: (id: string | number) => void
  setJobMapping: (jobId: string | number, instanceId: string) => void
  setJobStatus: (id: string, status: any) => void
  setListWorkflowName: (name: string | null) => void
  showAllWorkflows: () => void
  setPendingDeepLink: (deepLink: Store['pendingDeepLink']) => void

  setPendingNav: (v: any) => void
  setPendingOpenNewRequest: (value: boolean) => void
  setRawWorkflowData: (data: any) => void

  setRequestListTab: (tab: string) => void
  stopRefresh: () => void
  toggleMaximize: () => void
  toggleQuickFilter: (filter: string) => void
  updateProcessingProcess: (id: string | number, updates: any) => void
  workflowRefresh: () => void
}

const requestStore = create<Store>()(
  persist(
    (set) => ({
      activeQuickFilters: [],
      activeTabValue: null,
      isAutoOpenedRequest: false,
      isClosed: false,
      isMaximized: false,
      isRequestOpen: false,
      jobMappings: getInitialJobMappings(),
      jobStatuses: getInitialJobStatuses(),
      kanbanMissingFieldIds: [],
      allWorkflowsSignal: 0,
      listWorkflowName: null,
      newRequest: false,
      newRequestMeta: null,
      pendingDeepLink: null,
      pendingNav: null as null | { direction: 'NEXT' | 'PREV' },
      pendingOpenNewRequest: false,
      processingProcesses: [],
      quoteRegenerationPending: false,
      rawWorkflowData: null,
      reloadMeta: false,
      repoData: null,
      requestListTab: 'Inbox', // Default
      selectedItem: null,
      selectedWorkflow: null,
      selectedWorkflowId: null,
      summaryCache: {},
      addProcessingProcess: (process) =>
        set((state) => ({
          processingProcesses: [
            ...state.processingProcesses,
            {
              ...process,
              startTime: process.startTime || new Date().toISOString(),
            },
          ],
        })),
      cacheSummaryData: (reqNo, data) =>
        set((state) => ({
          summaryCache: { ...state.summaryCache, [reqNo]: data },
        })),
      clearPendingDeepLink: () => set({ pendingDeepLink: null }),
      clearPendingNav: () => set({ pendingNav: null }),
      clearQuickFilters: () => set({ activeQuickFilters: [] }),
      closeNewRequest: () => set({ newRequest: false, newRequestMeta: null }),
      closeRequest: () =>
        set((state) => ({
          activeTabValue: null,
          isAutoOpenedRequest: false,
          isClosed: !state.isClosed,
          isRequestOpen: false,
          kanbanMissingFieldIds: [],
          selectedItem: null, // Optional: clear data on close
        })),

      handleSetRepoData: (data) => set({ repoData: data }),
      openNewRequest: (title: string) =>
        set({
          newRequest: true,
          newRequestMeta: title,
          pendingOpenNewRequest: false,
        }),
      // FIX: Accept data when opening
      openRequest: (item, workflow, tab, missingFieldIds, options) =>
        set({
          activeTabValue: tab,
          isAutoOpenedRequest: Boolean(options?.isAutoOpened),
          isRequestOpen: true,
          kanbanMissingFieldIds: missingFieldIds || [],
          selectedItem: item,
          selectedWorkflow: workflow,
          selectedWorkflowId: workflow.id as number | string,
        }),
      removeProcessingProcess: (id) =>
        set((state) => ({
          // Callers pass string or numeric ids, and a tracker's processId is
          // swapped to the instanceId once the job reports one, so compare as
          // strings and also match the job id it was registered with.
          processingProcesses: state.processingProcesses.filter(
            (p) =>
              ![p.processId || p.id, p.apAgentJobId]
                .filter((pid) => pid !== null && pid !== undefined)
                .some((pid) => String(pid) === String(id)),
          ),
        })),
      showAllWorkflows: () => {
        // Remembered for when the Requests page mounts after navigation.
        try {
          sessionStorage.setItem('ezofis_requests_all_workflows', '1')
        } catch {
          // ignore
        }
        set((state) => ({
          activeTabValue: null,
          allWorkflowsSignal: state.allWorkflowsSignal + 1,
          isAutoOpenedRequest: false,
          isRequestOpen: false,
          newRequest: false,
          newRequestMeta: null,
          pendingDeepLink: null,
          pendingOpenNewRequest: false,
          selectedItem: null,
        }))
      },
      stopRefresh: () => set({ reloadMeta: false }),
      toggleMaximize: () =>
        set(({ isMaximized }) => ({ isMaximized: !isMaximized })),
      toggleQuickFilter: (filter) =>
        set((state) => ({
          activeQuickFilters: state.activeQuickFilters.includes(filter)
            ? state.activeQuickFilters.filter((f) => f !== filter)
            : [...state.activeQuickFilters, filter],
        })),
      updateProcessingProcess: (id, updates) =>
        set((state) => ({
          processingProcesses: state.processingProcesses.map((p) =>
            (p.processId || p.id) === id ? { ...p, ...updates } : p,
          ),
        })),
      workflowRefresh: () => set({ reloadMeta: true }),
      setJobMapping: (jobId, instanceId) =>
        set((state) => {
          const stringJobId = String(jobId)
          const nextMappings = {
            ...state.jobMappings,
            [stringJobId]: instanceId,
          }
          const jobKey = `job-${stringJobId}`
          const existingStatus = state.jobStatuses[jobKey]
          const nextStatuses = existingStatus
            ? { ...state.jobStatuses, [instanceId]: existingStatus }
            : state.jobStatuses
          try {
            localStorage.setItem(
              'v6_job_mappings',
              JSON.stringify(nextMappings),
            )
            if (existingStatus) {
              localStorage.setItem(
                'v6_job_statuses',
                JSON.stringify(nextStatuses),
              )
            }
          } catch {}
          return {
            jobMappings: nextMappings,
            jobStatuses: nextStatuses,
          }
        }),
      setJobStatus: (id, status) =>
        set((state) => {
          const nextStatuses = { ...state.jobStatuses, [id]: status }
          try {
            localStorage.setItem(
              'v6_job_statuses',
              JSON.stringify(nextStatuses),
            )
          } catch {}
          return { jobStatuses: nextStatuses }
        }),
      setListWorkflowName: (name) => set({ listWorkflowName: name }),
      setPendingDeepLink: (deepLink) => set({ pendingDeepLink: deepLink }),
      setPendingNav: (v) => set({ pendingNav: v }),
      setPendingOpenNewRequest: (value: boolean) =>
        set({ pendingOpenNewRequest: value }),
      setRawWorkflowData: (data) => set({ rawWorkflowData: data }),
      setRequestListTab: (tab) => set({ requestListTab: tab }),
    }),
    {
      name: 'v6_request_store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        activeQuickFilters: state.activeQuickFilters,
        activeTabValue: state.activeTabValue,
        isRequestOpen: state.isRequestOpen,
        newRequest: state.newRequest,
        newRequestMeta: state.newRequestMeta,
        rawWorkflowData: state.rawWorkflowData,
        requestListTab: state.requestListTab,
        selectedItem: state.selectedItem,
        selectedWorkflow: state.selectedWorkflow,
        selectedWorkflowId: state.selectedWorkflowId,
      }),
    },
  ),
)

export default requestStore
